import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { getProjectFiles, getProjects, Project, ProjectFile } from "../../services/Project";
import { COMPARABLE_FILE } from "./compare-utils";

export interface PickedFile {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
}

/** Choose a Word or PDF document that is already in one of the person's projects */
const ProjectFilePicker: React.FC<{
  open: boolean;
  onClose: () => void;
  onPick: (file: PickedFile) => void;
}> = ({ open, onClose, onPick }) => {
  const { t } = useI18n();
  const c = t.comparisons;
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [projectId, setProjectId] = useState("");
  const [files, setFiles] = useState<ProjectFile[] | null>(null);
  const [fileId, setFileId] = useState("");

  useEffect(() => {
    if (!open) return;
    setProjects(null);
    setProjectId("");
    setFiles(null);
    setFileId("");
    let cancelled = false;
    getProjects()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          // Starting a comparison needs edit access to the project
          setProjects(res.data.filter((p) => p.access !== "viewer" && p.fileCount > 0));
        } else {
          setProjects([]);
          toast.error(c.cp.pickerFailed);
        }
      })
      .catch(() => !cancelled && setProjects([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!projectId) return;
    setFiles(null);
    setFileId("");
    let cancelled = false;
    getProjectFiles(projectId)
      .then((res) => {
        if (cancelled) return;
        setFiles(
          res.success && res.data ? res.data.filter((f) => f.status === "ready" && COMPARABLE_FILE.test(f.name)) : [],
        );
      })
      .catch(() => !cancelled && setFiles([]));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const confirm = () => {
    const file = files?.find((f) => f.id === fileId);
    const project = projects?.find((p) => p.id === projectId);
    if (!file || !project) return;
    onPick({ id: file.id, name: file.name, projectId, projectName: project.name });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{c.cp.pickerTitle}</DialogTitle>
          <DialogDescription>{c.cp.pickerDescription}</DialogDescription>
        </DialogHeader>
        {projects == null ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">{c.cp.pickerNoProjects}</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pick-project">{c.cp.pickerProject}</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger id="pick-project">
                  <SelectValue placeholder={c.cp.pickerProjectPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {projectId && (
              <div className="space-y-2">
                <Label htmlFor="pick-file">{c.cp.pickerFile}</Label>
                {files == null ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : files.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{c.cp.pickerNoFiles}</p>
                ) : (
                  <Select value={fileId} onValueChange={setFileId}>
                    <SelectTrigger id="pick-file">
                      <SelectValue placeholder={c.cp.pickerFile} />
                    </SelectTrigger>
                    <SelectContent>
                      {files.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {c.cancel}
          </Button>
          <Button disabled={!fileId} onClick={confirm}>
            {c.cp.pickerUse}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectFilePicker;
